import { Component, ElementRef, EventEmitter, OnInit, Output, QueryList, ViewChildren, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { WorksheetService } from '../../../worksheet/services/worksheet.service';
import { map, Observable, Subject, takeUntil } from 'rxjs';
import { DateTimeUtils } from 'src/app/shared/util/date-time.utils';
import { FormService } from 'src/app/shared/services/form.service';
import { WorksheetResponse } from '../../../worksheet/models/worksheet-response.modal';
import { SharedService } from '../../../service/shared.service';
import { INotes } from '../../../models/inotes.modal';
import { MatDialog } from '@angular/material/dialog';
import { NotesComponent } from 'src/app/shared/components/notes/notes.component';
import { InstructionsComponent } from 'src/app/shared/components/instructions/instructions.component';
import { ProjectDetailsComponent } from 'src/app/shared/components/project-details/project-details.component';
import { MatTableDataSource } from '@angular/material/table';
import { AssignToLabtechComponent } from 'src/app/shared/components/assign-to-labtech/assign-to-labtech.component';
import { UserService } from 'src/app/shared/services/user.service';
import { SelectionModel } from '@angular/cdk/collections';
import { ExportTableService } from '../../../service/export-table.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { IDropdown } from 'src/app/shared/models/i-dropdown-type.modal';
import { CanComponentDeactivate } from 'src/app/shared/guard/unsaved-changes';
import { ConfirmationDialogComponent } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.component';
import { NativeSoilService } from 'src/app/features/worksheet/services/native-soil/native-soil.service';
import { NativeSoilType, NativeSoilWorkSheetTestResultsDto } from 'src/app/features/worksheet/models/native-soil-worksheet.modal';
import { messagesConfig } from 'src/app/shared/constants/message.constants';
import { DialogService } from 'src/app/shared/services/dialog.service';

@Component({
    selector: 'app-nativesoil-worksheet',
    templateUrl: './native-soil-worksheet.component.html',
    styleUrls: ['./native-soil-worksheet.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class NativeSoilWorksheetComponent implements OnInit, CanComponentDeactivate  {
  @Output() assigned = new EventEmitter<boolean>();
  @Output() rowDeletionSuccess = new EventEmitter<void>();
  @ViewChildren('weightInput') weightInputs!: QueryList<ElementRef>;
  @ViewChildren('trInput') trInputs!: QueryList<ElementRef>;
  @ViewChildren('cellInput') cellInputs!: QueryList<ElementRef>;
  selection = new SelectionModel<NativeSoilType>(true,[]);
  
  hasUnsavedChanges = false;
  projectId: string | null = null;
  projectStatuses: IDropdown[];
  private readonly destroyed$ = new Subject<boolean>();
  public dataSource = new MatTableDataSource<NativeSoilType>();
  public worksheets: NativeSoilType[];
  public worksheetRecords: WorksheetResponse | null = null;
  public note: INotes | undefined;
  isInstructionsConfirmed: boolean;
  userRoles: string[];
  daysLeft: number = 0;
  noteText: string | undefined = '';
  headerData: any;
  rowData: any;
  columns = [
    { key: 'select', displayName: '' },
    { key: 'inHouseId', displayName: 'In-House Id' },
    { key: 'sampleId', displayName: 'Sample Id' },
    { key: 'effort', displayName: 'Compaction Effort' },
    { key: 'depth', displayName: 'Depth' },
    { key: 'labSpecMoistureContent', displayName: 'Lab Spec Moisture Content' },
    { key: 'mouldPlusProbeTare', displayName: 'Mold + Probe' },
    { key: 'soilDiameter', displayName: 'Diameter' },
    { key: 'tubeHeight', displayName: 'Height' },
    { key: 'minus', displayName: 'Minus' },
    { key: 'loss', displayName: 'Loss' },
    { key: 'testNo', displayName: 'Test #' },
    { key: 'weight', displayName: 'Weight' },
    { key: 'tr', displayName: 'TR' },
    { key: 'addTestResult', displayName: 'Add' },
    { key: 'moistureTesting', displayName: 'Moisture (Current)' },
    { key: 'moistureClientSpecified', displayName: 'Moisture (Client)' },
    { key: 'densityLab', displayName: 'Density (Current)' },
    { key: 'densityClient', displayName: 'Density (Client)' },
    { key: 'assignedTo', displayName: 'Assigned To' },
    { key: 'status', displayName: 'Status' },
    { key: 'action', displayName: '' }
  ];
  displayedColumns = this.columns.map(c => c.key);

  constructor(
    private router: Router, 
    private route: ActivatedRoute,
    private worksheetService: WorksheetService,
    private formService: FormService,
    private sharedService: SharedService,
    public dialog: MatDialog,
    public userService: UserService,
    private exporttableService: ExportTableService,
    private snackBar: MatSnackBar,
    private nativeSoilService: NativeSoilService,
    private dialogService: DialogService
  ) { }
  
  canDeactivate(): Observable<boolean> {
    if (!this.hasUnsavedChanges) {
      return new Observable<boolean>(observer => {
        observer.next(true);
        observer.complete();
      });
    }

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      panelClass: 'Native-Soil-Worksheet',
      data: {
        title: 'Unsaved Changes',
        content: 'You have unsaved changes. Do you want to leave without saving?',
        cancelText: 'Stay',
        confirmText: 'Leave'
      },
      disableClose: true,
      enterAnimationDuration: '0ms',
      exitAnimationDuration: '0ms',
    });

    return dialogRef.afterClosed().pipe(
      map(result => result === 'YES')
    );
  }

  ngOnInit(): void {
    this.projectId = this.route.snapshot.queryParamMap.get('projectId');
    this.nativeSoilService.fetchProjectStatuses().subscribe({
      next: (projectStatues) => {
        this.projectStatuses = projectStatues;
      },
    });
    this.fetchWorksheet(this.projectId ?? '');
    this.fetchWorksheetNotes(this.projectId ?? '');
    this.fetchWorksheetDetails(this.projectId ?? '');
    this.userAccountAndRole();
  }

  viewSubmissionForm(): void {
    const params = {
      f: 'w',
    };
    const navigationExtras: NavigationExtras = {
      queryParams: params,
      state: {
        isShipmentReceived: this.worksheetRecords?.project?.storageCompartment
      }
    };

    this.formService.updateData(null);
    this.router.navigate([`/review/form/nativesoil/${this.projectId}`], navigationExtras);
  }

  completeWorksheet(): void {
    this.worksheetService.completeWorksheet(this.projectId ?? '').subscribe({
      next: (data) =>  {
        if(data === "Completed") {
          this.snackBar.open('Completed and Assigned to Lab Manager', 'DONE', {
            duration: 3000,
            verticalPosition: 'top',
            panelClass: ['default-snackbar'],
          });
        } else {
          this.snackBar.open('' + data, 'INFORMATION', {
            duration: 3000,
            verticalPosition: 'top',
            panelClass: ['default-snackbar'],
          });
        }
        this.fetchWorksheetDetails(this.projectId ?? '');
        this.fetchWorksheetNotes(this.projectId ?? '');
      },
      error: (err: any) => console.error(err)
    });
  }

  userAccountAndRole() {
    this.userService.userRole?.subscribe({
      next: (roles) => {
        this.userRoles = roles || [];
      }
    });
  }

  isAdmin(): boolean {
    return this.userRoles?.includes('Admin');
  }

  preventNumberInputChange(event: WheelEvent): void {
    (event.target as HTMLInputElement).blur();
  }

  toggleSelection(row: NativeSoilType) {
    this.selection.toggle(row);
  }

  isAllSelected() {
    return this.selection.selected.length === this.dataSource.data.length;
  }

  isIndeterminate() {
    return this.selection.selected.length > 0 && !this.isAllSelected();
  }

  isMultipleSelected(row: any): boolean {
    return this.selection.selected.length >= 1 && this.selection.selected.includes(row);
  }

  formatCastDate(value: any): string {
    return DateTimeUtils.formatDateAndTime(value, 'MM/dd/yyyy');
  }

  asReceivedWorksheet(): void {
    this.navigateToWorksheet('as-received-worksheet');
  }

  moistureConditioningWorksheet(): void {
    this.navigateToWorksheet('moisture-conditioning-worksheet');
  }

  singleProctorWorksheet(): void {
    this.navigateToWorksheet('single-proctor-worksheet');
  }

  compactionWorksheet(): void {
    this.navigateToWorksheet('compaction-worksheet');
  }

  thermalResistivityWorksheet(): void {
    this.navigateToWorksheet('native-soil-worksheet');
  }

  thermalResistivityHistory(): void {
    this.navigateToWorksheet('native-soil-history');
  }

  private navigateToWorksheet(route: string): void {
    this.canDeactivate().subscribe((canLeave: boolean) => {
      if (canLeave) {
        this.openWorksheet(route);
      }
    });
  }

  openWorksheet(route: string): void {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(
        [route],
        {
          queryParams: {
            projectId: this.projectId
          }
        }
      )
    );

    window.location.href = url;
  }

  printTargetWeights() {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(
        ['/native-soil-print'],
        { queryParams: { projectId: this.projectId } }
      )
    );

    window.open(url, '_blank');
  }

  roleCheck(roles: string[]): boolean {
    return roles.some((role) => this.userRoles.includes(role));
  }

  onMixDesignClick(element: any): void {
    const encodedCode = encodeURIComponent(element.mixDesignCode);
    window.open(`/mix-design/search?mixDesignCode=${encodedCode}`, '_blank');
  }

  masterToggle() {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      this.selection.select(...this.dataSource.data);
    }
  }

  isLastRowOfEffort(row: NativeSoilType, index: number): boolean {
    const nextRow = this.dataSource.data[index + 1];
    if (!nextRow) return false;

    return (
      nextRow.nativeSoilSampleId === row.nativeSoilSampleId &&
      nextRow.effort !== row.effort
    );
  }

  hasSingleNativeSoilTestResult(row: NativeSoilType): boolean {
      return (row.nativeSoilWorkSheetTestResults?.length ?? 0) === 1;
  }

  isLastRowOfSample(row: NativeSoilType, index: number): boolean {
    const currentSampleId = row.nativeSoilSampleId;
    const nextRow = this.dataSource.data[index + 1];
    if (!nextRow) return false;

    return nextRow.nativeSoilSampleId !== currentSampleId;
  }

  onFocusClearZero(obj: any, field: string) {
    if (obj[field] === 0) {
      obj[field] = null;
    }
  }

  onBlurRestoreZero(obj: any, field: string) {
    if (obj[field] === null || obj[field] === '') {
      obj[field] = 0;
    }
  }

  isTestingCompleted(): boolean {
    if (!this.projectStatuses || !this.worksheetRecords?.project.projectStatusTypeId) {
      return false;
    }

    const testingStatus = this.projectStatuses.find(
      status => status.label === 'Testing In Progress'
    );

    const isCorrectProjectStatus = testingStatus
      ? this.worksheetRecords.project.projectStatusTypeId === testingStatus.id
      : false;

    return isCorrectProjectStatus && this.allRowsValid();
  }

  allRowsValid(): boolean {
    if (!this.dataSource?.data?.length) return false;

    return this.dataSource.data.every(row =>
      this.calculateRowStatus(row)
    );
  }

  calculateRowStatus(row: NativeSoilType): boolean {
    const hasRequiredFields =
      Number(row.mouldPlusProbeTare ?? 0) > 0 &&
      Number(row.soilDiameter ?? 0) > 0 &&
      Number(row.tubeHeight ?? 0) > 0;

    const testResults = row.nativeSoilWorkSheetTestResults ?? [];

    const allTestsValid =
      testResults.length > 0 &&
      testResults.every(t =>
        Number(t.weight ?? 0) > 0 &&
        Number(t.tr ?? 0) > 0
      );

    row.status = hasRequiredFields && allTestsValid ? 'true' : 'false';
    return hasRequiredFields && allTestsValid;
  }

  getTestStatusClass(test: any): string {
    const weight = Number(test.weight ?? 0);
    const tr = Number(test.tr ?? 0);

    const hasWeight = weight > 0;
    const hasTr = tr > 0;

    if (hasWeight && hasTr) {
      return 'status-green';
    }
    return 'status-red';
  }

  onCellKeyPress(
    event: KeyboardEvent,
    row: any,
    testIndex: number,
    field: 'weight' | 'tr'
  ): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      const tests = row.nativeSoilWorkSheetTestResults || [];

      // Move to next test in the same row
      const nextTestIndex = testIndex + 1;
      if (nextTestIndex < tests.length) {
        this.focusField(row.id, nextTestIndex, field);
        return;
      }

      // Last test in this row → first test of next row
      const rows = this.dataSource.data;
      const currentRowIndex = rows.findIndex(r => r.id === row.id);

      if (currentRowIndex === -1) return;
      const nextRow = rows[currentRowIndex + 1];
      if (!nextRow?.id) return;
      const nextRowTests = nextRow.nativeSoilWorkSheetTestResults || [];

      if (nextRowTests.length > 0) {
        this.focusField(nextRow.id, 0, field);
      }
      return;
    }

    if (event.key == 'Tab') {
      event.preventDefault();
      const tests = row.nativeSoilWorkSheetTestResults || [];

      const fieldOrder = ['weight', 'tr'];
      const currentFieldIndex = fieldOrder.indexOf(field);

      let nextFieldIndex = currentFieldIndex + 1;
      let nextTestIndex = testIndex;

      // 1: move inside same test (weight -> tr)
      if (nextFieldIndex < fieldOrder.length) {
        this.focusField(row.id, testIndex, fieldOrder[nextFieldIndex]);
        return;
      }

      // 2: move to next test (TR -> next weight)
      nextFieldIndex = 0;
      nextTestIndex++;

      if (nextTestIndex < tests.length) {
        this.focusField(row.id, nextTestIndex, 'weight');
        return;
      }

      // 3: move to next row first weight
      this.focusNextRow(row.id);
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();

      const rows = this.dataSource.data;
      const currentRowIndex = rows.findIndex(r => r.id === row.id);

      if (currentRowIndex === -1) return;

      const nextRow = rows[currentRowIndex + 1];
      if (!nextRow || !nextRow.id) return;

      const nextRowTests = nextRow.nativeSoilWorkSheetTestResults || [];
      if (testIndex >= nextRowTests.length) return;

      this.focusField(nextRow.id, testIndex, 'weight');
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
    }
  }

  focusNextRow(currentRowId: string) {
    const rows = this.dataSource.data;
    const currentIndex = rows.findIndex(r => r.id === currentRowId);
    if (currentIndex === -1) return;

    const nextRow = rows[currentIndex + 1];
    if (!nextRow) return;

    const target = this.cellInputs?.toArray().find(i => {
      const el = i.nativeElement;

      return (
        el.getAttribute('data-row-id') === nextRow.id &&
        el.getAttribute('data-type') === 'inHouseId'
      );
    });

    target?.nativeElement?.focus();
  }

  focusField(rowId: string, testIndex: number, field: string) {
    const inputs = this.cellInputs.toArray();

    const target = inputs.find(i => {
      const el = i.nativeElement;
      return (
        el.getAttribute('data-row-id') === rowId &&
        el.getAttribute('data-test-index') === testIndex.toString() &&
        el.getAttribute('data-type') === field
      );
    });

    target?.nativeElement.focus();
  }

  onSingleFieldKeyPress(event: KeyboardEvent, row: NativeSoilType, field: 'inHouseId'): void {
    if (event.key !== 'Enter') { return; }

    event.preventDefault();
    const rows = this.dataSource.data;
    const currentRowIndex = rows.findIndex(r => r.id === row.id);

    if (currentRowIndex === -1) { return; }
    const nextRow = rows[currentRowIndex + 1];
    if (!nextRow?.id) { return; }

    const target = this.cellInputs?.toArray().find(input => {
      const el = input.nativeElement;

      return (
        el.getAttribute('data-row-id') === nextRow.id &&
        el.getAttribute('data-type') === field
      );
    });

    target?.nativeElement?.focus();
  }

  export() {
    const firstName = this.note?.updatedBy?.firstName || '';
    const lastName = this.note?.updatedBy?.lastName || '';
    const createdAt = this.note?.createdAt;
    const formattedTime = createdAt ? DateTimeUtils.getLocalDateTimeFromUTCDate(createdAt, 'MM/dd/yyyy, h:mm:ss a') : '';
    const notesUpdatedBy = `${firstName} ${lastName} ${formattedTime}`;

    // Same columns and order as the worksheet table, minus the selection, add-test and menu columns, assigned to, and
    // loss and test number, which don't apply once rows are sorted by moisture
    this.headerData = this.columns.filter(c => !['select', 'addTestResult', 'action', 'loss', 'testNo', 'assignedTo'].includes(c.key));
    this.rowData = this.buildExportRows();

    this.exporttableService.openInNewTab(
      this.headerData,
      this.rowData,
      this.worksheetRecords,
      notesUpdatedBy,
      this.noteText,
      'native-soil',
    );
  }

  // One export row per test result. Within each compaction-effort group (the rows between the worksheet's dotted
  // borders) the rows are sorted by current moisture, highest first, so every row repeats its worksheet row's values to stay
  // identifiable. The group's last row carries the worksheet's border: thick at the end of a sample, dotted otherwise
  private buildExportRows(): any[] {
    const groups: any[][] = [];
    let currentGroup: any[] = [];

    this.dataSource.data.forEach((row: any, index: number) => {
      const testResults: any[] = row.nativeSoilWorkSheetTestResults?.length ? row.nativeSoilWorkSheetTestResults : [{}];
      const isSingleTest = testResults.length === 1;
      const status = this.calculateRowStatus(row) ? 'Completed' : 'Not completed';

      testResults.forEach((testResult) => {
        currentGroup.push({
          inHouseId: row.inHouseId ?? '',
          sampleId: row.sampleId ?? '',
          effort: (row.effort?.toString() === '0' ? 'Tube' : row.effort) ?? '',
          depth: row.displayDepth ?? '',
          labSpecMoistureContent: row.labSpecMoistureContent ?? '',
          mouldPlusProbeTare: row.mouldPlusProbeTare ?? '',
          soilDiameter: row.soilDiameter ?? '',
          tubeHeight: row.tubeHeight ?? '',
          minus: row.minus ?? '',
          weight: testResult.weight ?? '',
          tr: testResult.tr ?? '',
          // A single test shows the lab spec moisture, as on the worksheet
          moistureTesting: (isSingleTest ? row.labSpecMoistureContent : testResult.moisture) ?? '',
          moistureClientSpecified: row.clientSpecifiedMoistureContent ?? '',
          densityLab: row.density ?? '',
          densityClient: row.clientSpecifiedDensity ?? '',
          status,
          lastRowOfSample: false,
          lastRowOfEffort: false,
        });
      });

      const endsSample = this.isLastRowOfSample(row, index);
      if (endsSample || this.isLastRowOfEffort(row, index) || index === this.dataSource.data.length - 1) {
        currentGroup.sort((a, b) => Number(b.moistureTesting || 0) - Number(a.moistureTesting || 0));
        const lastRow = currentGroup[currentGroup.length - 1];
        lastRow.lastRowOfSample = endsSample;
        lastRow.lastRowOfEffort = !endsSample && index < this.dataSource.data.length - 1;
        groups.push(currentGroup);
        currentGroup = [];
      }
    });

    return groups.flat();
  }

  saveTable(): void {
    const payload = this.preparePayload(this.dataSource.data);
    this.worksheetService.saveNativeSoilWorksheetTable(payload).subscribe({
      next: () => {
        this.hasUnsavedChanges = false;
        this.snackBar.open('Saved successfully', 'DONE', {
            duration: 3000,
            verticalPosition: 'top',
            panelClass: ['default-snackbar'],
        });
        this.ngOnInit();
      },
      error: (err: any) => {
        this.dialogService.open(messagesConfig.saveTableError);
      }
    });
  }

  preparePayload(data: NativeSoilType[]): any[] {
    return data.map(row => ({
      id: row.id,
      inHouseId: row.inHouseId,
      projectId: row.projectId,
      mouldPlusProbeTare: Number(row.mouldPlusProbeTare ?? 0),
      soilDiameter: Number(row.soilDiameter ?? 0),
      tubeHeight: Number(row.tubeHeight ?? 0),
      status: this.calculateRowStatus(row),

      nativeSoilWorkSheetTestResults: (row.nativeSoilWorkSheetTestResults || []).map(t => ({
        id: t.id ? t.id : null,
        nativeSoilWorkSheetId: row.id,
        testNumber: t.testNumber,
        weight: Number(t.weight ?? 0),
        tr: Number(t.tr ?? 0),
      }))
    }));
  }

  fetchWorksheetNotes(projectId: string): void {
    this.sharedService.fetchWSNotes(projectId);
    this.sharedService.notes$.subscribe((data) => {
      this.note = data;
    });
    this.sharedService.notesText$.subscribe((data) => {
      this.noteText = data;
    });
  }

  fetchWorksheetDetails(projectId: string): void {
    this.formService.fetchFormData(projectId, 'pre-cast').subscribe({
      next: (result) => {
        const project = result?.project;
        const company = result?.company;
        const comments = result?.comments;
        this.worksheetRecords = {
          project,
          company,
          comments
        };
        if(this.worksheetRecords.project.dueDate) {
          const dueDate = new Date(DateTimeUtils.getLocalDateTimeFromUTCDate(this.worksheetRecords.project.dueDate));
          this.daysLeft = this.findDateDifference(new Date(), dueDate);
        }
        this.isInstructionsConfirmed = this.worksheetRecords.project?.instructionsConfirmed ?? false;
      },
    });
  }

  private renumberTests(sample: NativeSoilType) {
    if (!sample.nativeSoilWorkSheetTestResults) {
      sample.nativeSoilWorkSheetTestResults = [];
      return;
    }

    sample.nativeSoilWorkSheetTestResults.forEach((t, i) => {
      t.testNumber = i + 1;
    });
  }

  addTestResult(sample: NativeSoilType) {
    if (!sample.nativeSoilWorkSheetTestResults) {
      sample.nativeSoilWorkSheetTestResults = [];
    }

    const newTest: NativeSoilWorkSheetTestResultsDto = {
      id: '',
      nativeSoilWorkSheetId: sample.id!,
      testNumber: 0,
      weight: 0,
      tr: 0,
    };

    sample.nativeSoilWorkSheetTestResults.push(newTest);

    this.renumberTests(sample);
    this.updateCalculations(sample);
    this.dataSource.data = [...this.dataSource.data];

    setTimeout(() => {
      const inputs = this.cellInputs?.toArray();
      if (!inputs?.length) return;

      const target = inputs.find(i => {
        const el = i.nativeElement;
        return (
          el.getAttribute('data-row-id') === sample.id &&
          el.getAttribute('data-type') === 'weight' &&
          el.getAttribute('data-test-index') ===
            (sample.nativeSoilWorkSheetTestResults!.length - 1).toString()
        );
      });

      target?.nativeElement?.focus();
    });
  }

  addTestResultToAll(): void {
    const rows = this.dataSource.data;
    if (!rows?.length) { return; }

    const targetRows = this.getHighestMoistureRowPerEffort(rows);

    targetRows.forEach((sample: NativeSoilType) => {
      if (!sample.nativeSoilWorkSheetTestResults) {
        sample.nativeSoilWorkSheetTestResults = [];
      }

      const newTest: NativeSoilWorkSheetTestResultsDto = {
        id: '',
        nativeSoilWorkSheetId: sample.id!,
        testNumber: 0,
        weight: 0,
        tr: 0,
      };

      sample.nativeSoilWorkSheetTestResults.push(newTest);

      this.renumberTests(sample);
      this.updateCalculations(sample);
    });

    // Refresh the table
    this.dataSource.data = [...rows];

    // Focus the newly added Weight field in the first row that received a test
    setTimeout(() => {
      const firstRow = targetRows[0];

      if (!firstRow?.id || !firstRow.nativeSoilWorkSheetTestResults?.length) {
        return;
      }

      const lastTestIndex = firstRow.nativeSoilWorkSheetTestResults.length - 1;

      const inputs = this.cellInputs?.toArray();
      if (!inputs?.length) { return; }

      const target = inputs.find(i => {
        const el = i.nativeElement;

        return (
          el.getAttribute('data-row-id') === firstRow.id &&
          el.getAttribute('data-type') === 'weight' &&
          el.getAttribute('data-test-index') ===
            lastTestIndex.toString()
        );
      });

      target?.nativeElement?.focus();
    });
  }

  // One row per sample + compaction effort group (the rows between dotted borders): the one with the highest lab spec moisture
  private getHighestMoistureRowPerEffort(rows: NativeSoilType[]): NativeSoilType[] {
    const highestByGroup = new Map<string, NativeSoilType>();

    rows.forEach((row) => {
      const key = `${row.nativeSoilSampleId}|${row.effort}`;
      const current = highestByGroup.get(key);
      if (!current || Number(row.labSpecMoistureContent ?? 0) > Number(current.labSpecMoistureContent ?? 0)) {
        highestByGroup.set(key, row);
      }
    });

    // Keep table order so focus lands on the topmost new test
    return rows.filter((row) => [...highestByGroup.values()].includes(row));
  }

  removeTestResult(element: NativeSoilType, index: number) {
    if (!element.nativeSoilWorkSheetTestResults) return;
    if (element.nativeSoilWorkSheetTestResults.length === 1) {
      const last = element.nativeSoilWorkSheetTestResults[0];

      last.weight = 0;
      last.tr = 0;

      this.updateCalculations(element);
      this.dataSource.data = [...this.dataSource.data];
      return;
    }

    element.nativeSoilWorkSheetTestResults.splice(index, 1);

    this.renumberTests(element);
    this.updateCalculations(element);
    this.dataSource.data = [...this.dataSource.data];
  }

  calculateDryWeight(element: any) {
    if (!element.nativeSoilWorkSheetTestResults || element.nativeSoilWorkSheetTestResults.length === 0) {
      element.dryWeight = 0;
      return;
    }

    const lastTestResult = element.nativeSoilWorkSheetTestResults[element.nativeSoilWorkSheetTestResults.length - 1];
    const totalTare = Number(element.tare ?? 0) + Number(element.mould ?? 0);
    const lastWeight = Number(lastTestResult.weight ?? 0);

    element.dryWeight = this.round2(lastWeight - totalTare);
  }

  calculateLoss(element: any) {
    if (!element.nativeSoilWorkSheetTestResults || element.nativeSoilWorkSheetTestResults.length === 0) {
      return;
    }

    element.nativeSoilWorkSheetTestResults.forEach((testResult: any, index: number) => {
      if (index === 0) {
        testResult.loss = 0;
      } else {
        const previousWeight = Number(element.nativeSoilWorkSheetTestResults[index - 1].weight ?? 0);
        const currentWeight = Number(testResult.weight ?? 0);

        testResult.loss = this.round2(previousWeight - currentWeight);
      }
    });
  }

  calculateDensity(element: any) {
    const soilHeight =
      Number(element.tubeHeight ?? 0) - Number(element.minus ?? 0);

    const drySoilWeight =
      Number(element.dryWeight ?? 0) - Number(element.mouldPlusProbeTare ?? 0);

    const diameter = Number(element.soilDiameter ?? 0);

    if (soilHeight <= 0 || diameter <= 0 || drySoilWeight <= 0) {
      element.soilHeight = 0;
      element.volume = 0;
      element.gramPerCmCube = 0;
      element.density = 0;
      return;
    }

    const volume = ((3.14159 * diameter * diameter) / 4000) * soilHeight;

    const gramPerCmCube = drySoilWeight / volume;

    const density = gramPerCmCube * 62.4;

    element.soilHeight = Number(soilHeight.toFixed(2));
    element.volume = Number(volume.toFixed(4));
    element.gramPerCmCube = Number(gramPerCmCube.toFixed(4));
    element.density = Number(density.toFixed(2));
  }

  calculateMoisture(element: any) {
    const dryWeight = Number(element.dryWeight ?? 0) - Number(element.mouldPlusProbeTare ?? 0);
    const totalTare = Number(element.mouldPlusProbeTare ?? 0);

    if (!dryWeight) return;
    element.nativeSoilWorkSheetTestResults.forEach((p: any) => {
      const wet = Number(p.weight ?? 0) - totalTare;

      if (!wet || wet <= 0) {
        p.moisture = 0;
        return;
      }
      const m = ((wet - dryWeight) / dryWeight) * 100;
      p.moisture = Number(m.toFixed(2));
    });
  }

  updateCalculations(element: any, pageLoad: boolean = false) {
    if(!pageLoad){
      this.hasUnsavedChanges = true;
    }
    this.calculateDryWeight(element);
    this.calculateLoss(element);
    this.calculateDensity(element);
    this.calculateMoisture(element);
    this.calculateRowStatus(element);
  }

  openViewNotesDialog(): void {
    const messageObject = {
      projectId: this.projectId,
      notes: this.note,
    };
    this.dialog
      .open(NotesComponent, {
        panelClass: 'app-dialog',
        data: messageObject,
        disableClose: true,
        width: '50%',
      })
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.fetchWorksheetNotes(this.projectId ?? '');
      });
  }

  openViewInstructionsDialog(): void {
    if (!this.worksheetRecords?.comments) return;

    this.dialog.open(InstructionsComponent, {
      panelClass: 'app-dialog',
      data: this.worksheetRecords.comments,
      disableClose: true,
      width: '50%',
    });
  }

  openProjectDetailsComponent(title: string, fieldName: string): void {
    if (!this.worksheetRecords?.project) return;
    const project = this.worksheetRecords.project;
    const messageObject = {
      projectId: this.projectId,
      title,
      fieldName,
      ovenDate: project.ovenDate,
      wetTestData: project.wetTestData,
      dryTestData: project.dryTestData,
    };
    this.dialog
      .open(ProjectDetailsComponent, {
        panelClass: 'app-dialog',
        data: messageObject,
        disableClose: true,
        width: '50%',
      })
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.fetchWorksheetDetails(this.projectId ?? '');
      });
  }

  openAssignToDialog(worksheetId: string): void {
    if (this.hasUnsavedChanges) {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        panelClass: 'worksheets',
        data: {
          title: 'Unsaved Changes',
          content: 'You have unsaved changes. Do you want to continue assigning without saving your changes?',
          cancelText: 'Cancel',
          confirmText: 'Continue'
        },
        disableClose: true
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result === 'YES') {
          this.openAssignDialogInternal(worksheetId);
        }
      });
      return;
    }
    
    this.openAssignDialogInternal(worksheetId);
  }

  openAssignDialogInternal(worksheetId: string): void {
    this.dialog
      .open(AssignToLabtechComponent, {
        data: {
          worksheetId,
          type: 'workSheet',
          multipleSelected : this.isMultipleSelected(worksheetId),
          worksheetRows : this.selection.selected,
          totalNumOfWorksheets: this.dataSource.data.length,
          role: this.userRoles
        },
        disableClose: true,
        width: '40%',
      })
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((assigned) => {
        if (assigned) {
          this.fetchWorksheet(this.projectId ?? '');
          this.rowDeletionSuccess.emit();
          this.assigned.emit(true);
          this.selection.clear();
        }
      });
  }

  findDateDifference(startDate: Date, endDate: Date) {
    const timeDifference = Math.abs(endDate.getTime() - startDate.getTime());
    const dayDifference = timeDifference / (1000 * 3600 * 24);
    return Math.trunc(dayDifference);
  }

  round2(value: number): number {
    return Number(value.toFixed(2));
  }

  reDirectUrl() {
    this.router.navigate(['dashboard']);
  }

  fetchWorksheet(projectId: string): void {
    this.nativeSoilService.getNativeSoilWorkSheetWillAllMoistureSheets(projectId).subscribe({
      next: (response: any) => {
        let initial = 1;
        console.log(response, 'worksheet api response');
        this.worksheets = (response?.nativeSoilWorksheets || []).filter((x: { reUse: any; }) => !x.reUse);
        this.worksheets.forEach((worksheet: any) => {
          if (!worksheet.groupOrder) {
            initial += 1;
          }
          worksheet.sequence = initial;
          if (worksheet.collectionDate) {
            worksheet.collectionDate = DateTimeUtils.formatDateAndTime(worksheet.collectionDate, 'MM/dd/yyyy');
          }
          worksheet.displayDepth = this.setDepth(worksheet);
          worksheet.assignedTo = this.mapUser(worksheet);
        });

        const orderedData = this.worksheets
          .map(w => {
            let results = w.nativeSoilWorkSheetTestResults ?? [];
            if (results.length === 0) {
              results = [this.createEmptyTestResult(w.id ?? '')];
            }

            return {
              ...w,
              nativeSoilWorkSheetTestResults: results
                .slice()
                .sort((a, b) => (a.testNumber ?? 0) - (b.testNumber ?? 0))
            };
          })
          .sort((a, b) => {
            // 1. Native worksheets always come before Tube worksheets
            if (a.worksheetType !== b.worksheetType) {
              if (a.worksheetType === 'Bulk') return -1;
              if (b.worksheetType === 'Bulk') return 1;
            }

            // NATIVE
            if (a.worksheetType === 'Bulk' && b.worksheetType === 'Bulk') {

              const sampleCompare = String(a.sampleId ?? '')
                .localeCompare(
                  String(b.sampleId ?? ''),
                  undefined,
                  {
                    numeric: true,
                    sensitivity: 'base'
                  }
                );

              if (sampleCompare !== 0) {
                return sampleCompare;
              }

              // 2. Depth
              const depthCompare = Number(a.depth ?? 0) - Number(b.depth ?? 0);
              if (depthCompare !== 0) {
                return depthCompare;
              }

              const nativeSampleCompare = String(a.nativeSoilSampleId ?? '')
                .localeCompare(
                  String(b.nativeSoilSampleId ?? ''),
                  undefined,
                  {
                    numeric: true,
                    sensitivity: 'base'
                  }
                );

              if (nativeSampleCompare !== 0) {
                return nativeSampleCompare;
              }

              const effortCompare =
                Number(a.effort ?? 0) - Number(b.effort ?? 0);

              if (effortCompare !== 0) {
                return effortCompare;
              }

              const moistureCompare =
                Number(b.labSpecMoistureContent ?? 0) -
                Number(a.labSpecMoistureContent ?? 0);

              if (moistureCompare !== 0) {
                return moistureCompare;
              }

              return 0;
            }

            // TUBE
            if (a.worksheetType === 'Tube' && b.worksheetType === 'Tube') {

              const sampleCompare = String(a.sampleId ?? '')
                .localeCompare(
                  String(b.sampleId ?? ''),
                  undefined,
                  {
                    numeric: true,
                    sensitivity: 'base'
                  }
                );

              if (sampleCompare !== 0) {
                return sampleCompare;
              }

              return Number(a.depth ?? 0) - Number(b.depth ?? 0);
            }

            return 0;
          });

        this.dataSource.data = orderedData;
        this.dataSource.data = orderedData;
        this.dataSource.data.forEach(e => this.updateCalculations(e, true));
        this.rowData = this.dataSource.data;
      },
      error: (err) => {
        console.error('Error fetching worksheets:', err);
      },
    });
  }

  setDepth(elem: any): string {
    let depthRange = '';
    if (elem.depth !== null) {
      depthRange = `${elem.depth}`;
    }
    if (elem.endDepth) {
      if (elem.depth !== null && elem.endDepth === null) {
        depthRange = `${elem.depth}`;
      }
      if (elem.endDepth !== null && elem.depth !== null) {
        depthRange = `${elem.depth}-${elem.endDepth}`;
      }
    }
    return depthRange;
  }

  private mapUser(worksheet: any): string | null {
    if (worksheet.assignedToId) {
      return `${worksheet.assignedTo.firstName} ${worksheet.assignedTo.lastName}`;
    }
    return null;
  }

  createEmptyTestResult(nativeSoilWorkSheetId: string): NativeSoilWorkSheetTestResultsDto {
    return {
      id: '',
      nativeSoilWorkSheetId: nativeSoilWorkSheetId,
      testNumber: 1,
      weight: 0,
      tr: 0,
      createdAt: null,
      updatedAt: null,
      createdById: null,
      updatedById: null
    };
  }
}
